import React from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

const CYAN = "#17c1e8";

// One style per intent. Tones match GoalStatusChip / GoalActionButton.
const VARIANTS = {
  // Main action on a screen.
  primary: {
    color: "#fff",
    backgroundColor: CYAN,
    "&:hover": {
      backgroundColor: "#0fb0d6",
    },
  },
  // Cancel and other neutral actions.
  secondary: {
    color: "#344767",
    backgroundColor: "#fff",
    border: "1px solid #dfe3ea",
    "&:hover": { backgroundColor: "#f4f6f9", borderColor: "#c9d0db" },
  },
  // Supporting action next to a primary one (Save as Draft, Save Marks).
  tonal: {
    color: "#0b7c99",
    backgroundColor: "#e0f5fb",
    "&:hover": { backgroundColor: "#c9eef8" },
  },
  // Quiet inline action (Back, Add Goal).
  text: {
    color: "#0b7c99",
    backgroundColor: "transparent",
    "&:hover": { backgroundColor: "#e0f5fb" },
  },
  danger: {
    color: "#fff",
    backgroundColor: "#ea0606",
    "&:hover": {
      backgroundColor: "#d00505",
    },
  },
  dangerSoft: {
    color: "#c01616",
    backgroundColor: "#fde7e7",
    "&:hover": { backgroundColor: "#fbd3d3" },
  },
};

// Goals-screen button: rounded, sentence case, optional leading icon, and a
// spinner that sits beside the label so the button doesn't change width.
function GoalButton({ variant, icon, loading, disabled, onClick, children, size, ...rest }) {
  const compact = size === "small";
  return (
    <Button
      disableElevation
      disabled={disabled || loading}
      onClick={onClick}
      {...rest}
      sx={{
        minHeight: compact ? 32 : 40,
        px: compact ? 1.5 : 2.5,
        borderRadius: "10px",
        fontSize: compact ? 12 : 14,
        fontWeight: 600,
        textTransform: "none",
        letterSpacing: 0.2,
        whiteSpace: "nowrap",
        gap: 0.75,
        transition: "background-color 0.15s ease",
        ...VARIANTS[variant],
        // The app theme recolours text buttons on hover/focus and fades active
        // ones (e.g. when focus returns here after a dialog closes). Pin the
        // variant's own colours in every state; the doubled class outranks the
        // theme's `:active:focus` / `:focus:not(:hover)` rules.
        "&.MuiButton-root.MuiButtonBase-root": {
          "&:focus, &:focus-visible, &:active, &:active:focus, &:focus:not(:hover)": {
            color: VARIANTS[variant].color,
            backgroundColor: VARIANTS[variant].backgroundColor,
            opacity: 1,
            transform: "none",
          },
          "&:hover, &:active:hover": {
            color: VARIANTS[variant].color,
            backgroundColor: VARIANTS[variant]["&:hover"].backgroundColor,
            opacity: 1,
            transform: "none",
          },
        },
        "&.MuiButton-root .MuiIcon-root": { fontSize: "18px !important", marginTop: 0 },
        "&.Mui-disabled": {
          color: "#a4adbd",
          backgroundColor: variant === "text" ? "transparent" : "#eef0f4",
          border: variant === "secondary" ? "1px solid #e9ecef" : "none",
          boxShadow: "none",
          cursor: "not-allowed",
          pointerEvents: "auto",
          opacity: 1,
          // While loading, keep the variant's own colours (just dimmed).
          ...(loading && {
            color: VARIANTS[variant].color,
            backgroundColor: VARIANTS[variant].backgroundColor,
            border: VARIANTS[variant].border || "none",
            opacity: 0.8,
            cursor: "progress",
          }),
        },
      }}
    >
      {loading ? (
        <CircularProgress size={16} color="inherit" />
      ) : (
        icon && <Icon sx={{ fontSize: "18px !important" }}>{icon}</Icon>
      )}
      {children}
    </Button>
  );
}

GoalButton.defaultProps = {
  variant: "primary",
  icon: null,
  loading: false,
  disabled: false,
  onClick: undefined,
  size: "medium",
};
GoalButton.propTypes = {
  variant: PropTypes.oneOf(["primary", "secondary", "tonal", "text", "danger", "dangerSoft"]),
  icon: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  size: PropTypes.oneOf(["small", "medium"]),
  children: PropTypes.node.isRequired,
};

export default GoalButton;
